import api from './api';

class TenantService {
  async getTenants() {
    const response = await api.get('/tenants');
    return response.data;
  }

  async getInstitutionTypes() {
    const response = await api.get('/tenants/institution-types');
    return response.data;
  }

  async createTenant(tenantData) {
    const response = await api.post('/tenants', tenantData);
    return response.data;
  }

  async updateTenantById(tenantId, tenantData) {
    const response = await api.put(`/tenants/${tenantId}`, tenantData);
    return response.data;
  }

  async getCurrentTenant() {
    const response = await api.get('/tenants/current');
    if (response.data?.currency) {
      localStorage.setItem('tenant_currency', response.data.currency);
    }
    if (response.data?.institution_config) {
      localStorage.setItem('tenant_institution_config', JSON.stringify(response.data.institution_config));
    }
    return response.data;
  }

  async getTenantInfo() {
    const response = await api.get('/tenants/info');
    return response.data;
  }

  async updateTenant(tenantData) {
    const response = await api.put('/tenants', tenantData);
    return response.data;
  }

  async getTenantStatistics() {
    const response = await api.get('/tenants/statistics');
    return response.data;
  }

  async getTenantSettings() {
    const response = await api.get('/tenants/settings');
    if (response.data?.general?.currency) {
      localStorage.setItem('tenant_currency', response.data.general.currency);
    }
    return response.data;
  }

  async updateTenantSettings(settings) {
    const response = await api.put('/tenants/settings', settings);
    const currency = response.data?.settings?.general?.currency || settings?.general?.currency;
    if (currency) {
      localStorage.setItem('tenant_currency', currency);
      window.dispatchEvent(new CustomEvent('tenant-currency-changed', {
        detail: { currency }
      }));
    }
    return response.data;
  }

  async getTenantFinancialSummary() {
    const response = await api.get('/tenants/financial-summary');
    return response.data;
  }

  async getTenantDashboardData() {
    const response = await api.get('/tenants/dashboard');
    return response.data;
  }

  async getTenantActivityLog(params = {}) {
    const response = await api.get('/tenants/activity-log', { params });
    return response.data;
  }

  async backupTenantData() {
    const response = await api.post('/tenants/backup');
    return response.data;
  }

  async restoreTenantData(backupData) {
    const response = await api.post('/tenants/restore', backupData);
    return response.data;
  }

  async exportTenantData(format = 'json') {
    const response = await api.get('/tenants/export', {
      params: { format },
      responseType: 'blob'
    });
    return response.data;
  }

  async updateTenantLogo(logoFile) {
    const formData = new FormData();
    formData.append('logo', logoFile);
    
    const response = await api.post('/tenants/logo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }

  async getTenantNotifications() {
    const response = await api.get('/tenants/notifications');
    return response.data;
  }

  async markNotificationAsRead(notificationId) {
    const response = await api.put(`/tenants/notifications/${notificationId}/read`);
    return response.data;
  }

  async markAllNotificationsAsRead() {
    const response = await api.put('/tenants/notifications/read-all');
    return response.data;
  }

  async getTenantUsersReport() {
    const response = await api.get('/tenants/reports/users');
    return response.data;
  }

  async getTenantFinancialReport(params = {}) {
    const response = await api.get('/tenants/reports/financial', { params });
    return response.data;
  }

  async getTenantPerformanceMetrics() {
    const response = await api.get('/tenants/performance');
    return response.data;
  }

  // Configuration et paramètres
  async getTenantConfiguration() {
    const response = await api.get('/tenants/configuration');
    return response.data;
  }

  async updateTenantConfiguration(config) {
    const response = await api.put('/tenants/configuration', config);
    return response.data;
  }

  async getTenantPaymentMethods() {
    const response = await api.get('/tenants/payment-methods');
    return response.data;
  }

  async updatePaymentMethod(methodId, methodData) {
    const response = await api.put(`/tenants/payment-methods/${methodId}`, methodData);
    return response.data;
  }

  async addPaymentMethod(methodData) {
    const response = await api.post('/tenants/payment-methods', methodData);
    return response.data;
  }

  async deletePaymentMethod(methodId) {
    const response = await api.delete(`/tenants/payment-methods/${methodId}`);
    return response.data;
  }

  // Gestion des taux d'intérêt
  async getInterestRates(params = {}) {
    const response = await api.get('/tenants/interest-rates', { params });
    return response.data;
  }

  async updateInterestRate(rateId, rateData) {
    const response = await api.put(`/tenants/interest-rates/${rateId}`, rateData);
    return response.data;
  }

  async createInterestRate(rateData) {
    const response = await api.post('/tenants/interest-rates', rateData);
    return response.data;
  }

  // Alertes et notifications système
  async getSystemAlerts() {
    const response = await api.get('/tenants/system-alerts');
    return response.data;
  }

  async acknowledgeAlert(alertId) {
    const response = await api.put(`/tenants/system-alerts/${alertId}/acknowledge`);
    return response.data;
  }

  // Audit et conformité
  async getAuditLog(params = {}) {
    const response = await api.get('/tenants/audit-log', { params });
    return response.data;
  }

  async getComplianceReport() {
    const response = await api.get('/tenants/compliance-report');
    return response.data;
  }

  // Intégrations et API
  async getApiKeys() {
    const response = await api.get('/tenants/api-keys');
    return response.data;
  }

  async createApiKey(keyData) {
    const response = await api.post('/tenants/api-keys', keyData);
    return response.data;
  }

  async revokeApiKey(keyId) {
    const response = await api.delete(`/tenants/api-keys/${keyId}`);
    return response.data;
  }

  // Sauvegarde et récupération
  async getBackupHistory() {
    const response = await api.get('/tenants/backup-history');
    return response.data;
  }

  async downloadBackup(backupId) {
    const response = await api.get(`/tenants/backups/${backupId}/download`, {
      responseType: 'blob'
    });
    return response.data;
  }

  // Services de support
  async createSupportTicket(ticketData) {
    const response = await api.post('/tenants/support-tickets', ticketData);
    return response.data;
  }

  async getSupportTickets(params = {}) {
    const response = await api.get('/tenants/support-tickets', { params });
    return response.data;
  }

  async getSupportTicket(ticketId) {
    const response = await api.get(`/tenants/support-tickets/${ticketId}`);
    return response.data;
  }

  async updateSupportTicket(ticketId, updateData) {
    const response = await api.put(`/tenants/support-tickets/${ticketId}`, updateData);
    return response.data;
  }

  // Métriques et analyses
  async getUsageMetrics(params = {}) {
    const response = await api.get('/tenants/usage-metrics', { params });
    return response.data;
  }

  async getGrowthMetrics() {
    const response = await api.get('/tenants/growth-metrics');
    return response.data;
  }

  async getClientRetentionMetrics() {
    const response = await api.get('/tenants/client-retention');
    return response.data;
  }

  // Gestion des abonnements
  async getSubscriptionInfo() {
    const response = await api.get('/tenants/subscription');
    return response.data;
  }

  async updateSubscription(planData) {
    const response = await api.put('/tenants/subscription', planData);
    return response.data;
  }

  async getBillingHistory() {
    const response = await api.get('/tenants/billing-history');
    return response.data;
  }

  async downloadInvoice(invoiceId) {
    const response = await api.get(`/tenants/invoices/${invoiceId}/download`, {
      responseType: 'blob'
    });
    return response.data;
  }
}

export default new TenantService();
